import { useState } from 'react'
import { isAxiosError } from 'axios'
import { Modal } from '@/components/ui/Modal'
import { CustomCardForm } from './CustomCardForm'
import type { CustomCardValues } from './CustomCardForm'
import { updateCustomFlashcard, deleteCustomFlashcard } from '@/api/customFlashcards'
import { cn } from '@/utils/cn'
import type { CustomVocabularyEntry } from '@/types/vocabulary'

interface EditCustomCardModalProps {
  entry: CustomVocabularyEntry
  languageId: number
  onClose: () => void
  onSuccess: () => void
}

export function EditCustomCardModal({ entry, languageId, onClose, onSuccess }: EditCustomCardModalProps) {
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  function handleUpdate(values: CustomCardValues) {
    setSubmitting(true)
    setErrors({})
    updateCustomFlashcard(entry.custom_flashcard_id, languageId, {
      lemma: values.lemma,
      translation: values.translation,
      part_of_speech: values.part_of_speech || null,
      gender: values.gender || null,
      example_sentence: values.example_sentence || null,
      notes: values.notes || null,
    })
      .then(() => {
        onSuccess()
        onClose()
      })
      .catch((err: unknown) => {
        if (isAxiosError(err) && err.response?.data?.errors) {
          setErrors(err.response.data.errors as Record<string, string[]>)
        } else {
          setErrors({ base: ['Something went wrong. Please try again.'] })
        }
      })
      .finally(() => setSubmitting(false))
  }

  function handleDelete() {
    if (!confirmingDelete) {
      setConfirmingDelete(true)
      return
    }
    setSubmitting(true)
    deleteCustomFlashcard(entry.custom_flashcard_id, languageId)
      .then(() => {
        onSuccess()
        onClose()
      })
      .catch(() => setErrors({ base: ['Could not delete the card. Please try again.'] }))
      .finally(() => setSubmitting(false))
  }

  return (
    <Modal open onClose={onClose} title="Edit custom card">
      <CustomCardForm
        initial={{
          lemma: entry.lemma,
          translation: entry.translations[0] ?? '',
          part_of_speech: entry.part_of_speech ?? '',
          gender: entry.gender ?? '',
          example_sentence: entry.example_sentence ?? '',
          notes: entry.notes ?? '',
        }}
        submitLabel="Save"
        submitting={submitting}
        errors={errors}
        onSubmit={handleUpdate}
        footer={
          <button
            type="button"
            onClick={handleDelete}
            disabled={submitting}
            className={cn(
              'px-4 py-2 rounded-full text-xs font-light min-h-11',
              'border border-error/40 text-error hover:bg-error/5',
              'transition-colors duration-200 disabled:opacity-50',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
            )}
          >
            {confirmingDelete ? 'Confirm delete' : 'Delete'}
          </button>
        }
      />
    </Modal>
  )
}
